import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, viewQuery } from "@/api/views";
import { brandHead } from "@/lib/head";
import ContractEditPage from "@/pages/app/contracts/[id]/edit/page";
import ContractPage from "@/pages/app/contracts/[id]/page";
import ContractPrintPage from "@/pages/app/contracts/[id]/print/page";
import ContractStatementPage from "@/pages/app/contracts/[id]/statement/page";
import ContractStatementPrintPage from "@/pages/app/contracts/[id]/statement/print/page";
import ContractsPage from "@/pages/app/contracts/page";

import { appRoute, APP_BOUNDARIES } from "./w0b";

interface ContractsSearch {
  q?: string;
  status?: string;
  page?: string;
}

interface StatementSearch {
  by?: string;
}

// 주소의 쿼리는 사람이 고칠 수 있다 — 글자·숫자만 문자열로 받고 나머지는 버린다(값 판정은 core 가 한다).
const textOf = (value: unknown): string | undefined =>
  typeof value === "string" ? value : typeof value === "number" ? String(value) : undefined;

const contractsQuery = (search: ContractsSearch) => viewQuery("contracts", search);
const contractQuery = (id: string) => viewQuery("contract", { id });
const statementQuery = (id: string, search: StatementSearch) => viewQuery("contract-statement", { id, ...search });

const ContractsRouteComponent = () => {
  const search = contractsRoute.useSearch();
  const { data } = useSuspenseQuery(contractsQuery(search));
  return <ContractsPage data={data} />;
};

export const contractsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts",
  validateSearch: (search: Record<string, unknown>): ContractsSearch => ({ q: textOf(search.q), status: textOf(search.status), page: textOf(search.page) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, contractsQuery(deps)),
  head: () => brandHead("계약"),
  component: ContractsRouteComponent,
  ...APP_BOUNDARIES,
});

const ContractRouteComponent = () => {
  const { id } = contractRoute.useParams();
  const { data } = useSuspenseQuery(contractQuery(id));
  return <ContractPage data={data} />;
};

export const contractRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id",
  loader: ({ context, params }) => loadView(context.queryClient, contractQuery(params.id)),
  // 제목은 읽어 둔 계약번호 — 없으면 「계약」.
  head: ({ match, params }) => brandHead(match.context.queryClient.getQueryData(contractQuery(params.id).queryKey)?.title ?? "계약"),
  component: ContractRouteComponent,
  ...APP_BOUNDARIES,
});

const ContractEditRouteComponent = () => {
  const { id } = contractEditRoute.useParams();
  const { data } = useSuspenseQuery(viewQuery("contract-edit", { id }));
  return <ContractEditPage data={data} />;
};

export const contractEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/edit",
  loader: ({ context, params }) => loadView(context.queryClient, viewQuery("contract-edit", { id: params.id })),
  head: () => brandHead("계약 편집"),
  component: ContractEditRouteComponent,
  ...APP_BOUNDARIES,
});

const ContractPrintRouteComponent = () => {
  const { id } = contractPrintRoute.useParams();
  const { data } = useSuspenseQuery(viewQuery("contract-print", { id }));
  return <ContractPrintPage data={data} />;
};

export const contractPrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/print",
  loader: ({ context, params }) => loadView(context.queryClient, viewQuery("contract-print", { id: params.id })),
  head: () => brandHead("계약서 인쇄"),
  component: ContractPrintRouteComponent,
  ...APP_BOUNDARIES,
});

const validateStatementSearch = (search: Record<string, unknown>): StatementSearch => ({ by: textOf(search.by) });

const ContractStatementRouteComponent = () => {
  const { id } = contractStatementRoute.useParams();
  const search = contractStatementRoute.useSearch();
  const { data } = useSuspenseQuery(statementQuery(id, search));
  return <ContractStatementPage data={data} />;
};

export const contractStatementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/statement",
  validateSearch: validateStatementSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, statementQuery(params.id, deps)),
  // 제목은 읽어 둔 계약번호 — 없으면 「현재 내역서」.
  head: ({ match, params }) =>
    brandHead(match.context.queryClient.getQueryData(statementQuery(params.id, match.search).queryKey)?.title ?? "현재 내역서"),
  component: ContractStatementRouteComponent,
  ...APP_BOUNDARIES,
});

const ContractStatementPrintRouteComponent = () => {
  const { id } = contractStatementPrintRoute.useParams();
  const search = contractStatementPrintRoute.useSearch();
  const { data } = useSuspenseQuery(statementQuery(id, search));
  return <ContractStatementPrintPage data={data} />;
};

export const contractStatementPrintRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contracts/$id/statement/print",
  validateSearch: validateStatementSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, statementQuery(params.id, deps)),
  head: () => brandHead("현재 내역서 인쇄"),
  component: ContractStatementPrintRouteComponent,
  ...APP_BOUNDARIES,
});

// g2a 배치의 라우트 — 계약 목록·상세·편집·인쇄·현재 내역서(화면·인쇄).
export const routes: AnyRoute[] = [
  contractsRoute,
  contractRoute,
  contractEditRoute,
  contractPrintRoute,
  contractStatementRoute,
  contractStatementPrintRoute,
];
